import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Package, Plus, Share2, Trash2, Edit3, Copy, ExternalLink,
  Loader2, X, Check, Search, ShieldX, FileDown, FileText,
} from "lucide-react";
import { toast } from "sonner";

const WRITE_ROLES = ["owner", "admin", "project_manager", "superintendent"];

const TEMPLATES = [
  { v: "custom", l: "Custom" },
  { v: "earthwork", l: "Earthwork" },
  { v: "drainage", l: "Drainage" },
  { v: "paving", l: "Paving" },
  { v: "lighting", l: "Lighting" },
  { v: "structures", l: "Structures" },
  { v: "utilities", l: "Utilities" },
  { v: "mot", l: "MOT" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return iso; }
}

export default function ProjectPackages() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [project, setProject] = useState(null);
  const [packages, setPackages] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareModalPkg, setShareModalPkg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pks, sh] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/sheet-packages?project_id=${projectId}`),
        api.get(`/projects/${projectId}/sheets`),
      ]);
      setProject(p.data);
      setPackages(pks.data || []);
      setSheets(sh.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load packages");
    } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ name: "", description: "", template: "custom", sheet_ids: [] }); setEditorOpen(true); };
  const openEdit = (pkg) => { setEditing({ ...pkg, sheet_ids: pkg.sheet_ids || [] }); setEditorOpen(true); };

  const saveEditor = async () => {
    const e = editing;
    if (!e?.name?.trim()) { toast.error("Name required"); return; }
    try {
      if (e.id) {
        await api.patch(`/sheet-packages/${e.id}`, {
          name: e.name.trim(),
          description: (e.description || "").trim(),
          template: e.template || "custom",
          sheet_ids: e.sheet_ids,
        });
        toast.success("Package updated");
      } else {
        await api.post(`/sheet-packages`, {
          project_id: projectId,
          name: e.name.trim(),
          description: (e.description || "").trim(),
          template: e.template || "custom",
          sheet_ids: e.sheet_ids,
        });
        toast.success("Package created");
      }
      setEditorOpen(false); setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package? This revokes any active share links.")) return;
    try {
      await api.delete(`/sheet-packages/${id}`);
      toast.success("Package deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const [generatingId, setGeneratingId] = useState(null);
  const generatePdf = async (pkg) => {
    setGeneratingId(pkg.id);
    try {
      const r = await api.post(`/sheet-packages/${pkg.id}/generate-pdf`);
      toast.success(`PDF ready · ${r.data.page_count} pages`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    } finally { setGeneratingId(null); }
  };

  const downloadPdf = async (pkg) => {
    try {
      const r = await api.get(`/sheet-packages/${pkg.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const filename = pkg?.package_pdf?.filename || `${pkg.name || "package"}.pdf`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="project-packages-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to={`/project/${projectId}`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
          <ArrowLeft size={12} /> {project?.name || "Project"}
        </Link>
        {canWrite && (
          <button onClick={openNew} className="btn btn-primary inline-flex items-center gap-1.5 text-xs" data-testid="create-package-btn">
            <Plus size={12} /> New package
          </button>
        )}
      </div>

      <div className="fop-surface-2 p-5 rounded-lg mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Intelligence · Sheet Packages</div>
        <h1 className="font-display text-2xl text-[var(--fop-text)]">Packages</h1>
        <p className="text-xs text-[var(--fop-text-3)] mt-1 max-w-2xl">
          Group sheets into operational packages (Paving, Drainage, etc.) for crews and subcontractors. Share each package via a tokenised expiring link.
        </p>
      </div>

      {loading ? (
        <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : packages.length === 0 ? (
        <div className="fop-surface-2 p-8 rounded-lg text-center text-sm text-[var(--fop-text-3)]" data-testid="packages-empty">
          No packages yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="packages-grid">
          {packages.map((pkg) => (
            <div key={pkg.id} className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border)] hover:border-[var(--fop-border-hi)] transition-colors" data-testid={`package-card-${pkg.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-[var(--fop-primary)]" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">{pkg.template || "custom"}</span>
                  </div>
                  <div className="font-display text-base text-[var(--fop-text)] truncate mt-1">{pkg.name}</div>
                  {pkg.description && <div className="text-xs text-[var(--fop-text-3)] line-clamp-2 mt-1">{pkg.description}</div>}
                </div>
                <span className="text-[10px] font-mono text-[var(--fop-text-4)] shrink-0">{(pkg.sheet_ids || []).length} sheets</span>
              </div>
              <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-2">Created {fmtDate(pkg.created_at)}</div>
              {pkg.package_pdf && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono text-emerald-400" data-testid={`pdf-ready-${pkg.id}`}>
                  <FileText size={11} /> PDF · {pkg.package_pdf.page_count || 0} pages · {Math.round((pkg.package_pdf.size_bytes || 0) / 1024)} KB
                </div>
              )}
              <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                <button onClick={() => setShareModalPkg(pkg)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid={`share-pkg-${pkg.id}`}>
                  <Share2 size={11} /> Share
                </button>
                {canWrite && (
                  <button
                    onClick={() => generatePdf(pkg)}
                    disabled={generatingId === pkg.id}
                    className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5"
                    data-testid={`generate-pdf-${pkg.id}`}
                  >
                    {generatingId === pkg.id ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
                    {pkg.package_pdf ? "Regenerate PDF" : "Generate PDF"}
                  </button>
                )}
                {pkg.package_pdf && (
                  <button
                    onClick={() => downloadPdf(pkg)}
                    className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 text-[var(--fop-primary)]"
                    data-testid={`download-pdf-${pkg.id}`}
                  >
                    <FileDown size={11} /> Download PDF
                  </button>
                )}
                {canWrite && (
                  <>
                    <button onClick={() => openEdit(pkg)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid={`edit-pkg-${pkg.id}`}>
                      <Edit3 size={11} /> Edit
                    </button>
                    <button onClick={() => deletePackage(pkg.id)} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 text-red-400" data-testid={`delete-pkg-${pkg.id}`}>
                      <Trash2 size={11} /> Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && editing && (
        <PackageEditor
          editing={editing}
          setEditing={setEditing}
          sheets={sheets}
          onCancel={() => { setEditorOpen(false); setEditing(null); }}
          onSave={saveEditor}
        />
      )}

      {shareModalPkg && (
        <ShareModal
          packageId={shareModalPkg.id}
          name={shareModalPkg.name}
          canWrite={canWrite}
          onClose={() => setShareModalPkg(null)}
        />
      )}
    </div>
  );
}

function PackageEditor({ editing, setEditing, sheets, onCancel, onSave }) {
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = new Set(editing.sheet_ids || []);
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return sheets;
    return sheets.filter((s) =>
      (s.sheet_number || "").toLowerCase().includes(f) ||
      (s.sheet_title || "").toLowerCase().includes(f) ||
      (s.discipline || "").toLowerCase().includes(f));
  }, [filter, sheets]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setEditing({ ...editing, sheet_ids: [...next] });
  };

  const submit = async () => {
    setSaving(true);
    try { await onSave(); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div className="fop-surface-2 rounded-lg w-full max-w-3xl border border-[var(--fop-border-hi)] flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()} data-testid="package-editor">
        <div className="p-5 border-b border-[var(--fop-border)]">
          <h3 className="font-display text-lg text-[var(--fop-text)]">{editing.id ? "Edit Package" : "New Package"}</h3>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Name</label>
            <input
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              data-testid="pkg-name"
              className="w-full px-3 py-2 mb-3 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)]"
              placeholder="e.g., Phase 1 Paving"
            />
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Template</label>
            <select
              value={editing.template || "custom"}
              onChange={(e) => setEditing({ ...editing, template: e.target.value })}
              data-testid="pkg-template"
              className="w-full px-3 py-2 mb-3 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)]"
            >
              {TEMPLATES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Description</label>
            <textarea
              value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              rows={4}
              data-testid="pkg-description"
              className="w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)] resize-none"
            />
            <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-3">{(editing.sheet_ids || []).length} sheet{(editing.sheet_ids || []).length === 1 ? "" : "s"} selected</div>
          </div>
          <div className="flex flex-col min-h-0">
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Sheets</label>
            <div className="relative mb-2">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--fop-text-4)]" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by number, title, discipline"
                data-testid="pkg-sheet-filter"
                className="w-full pl-7 pr-3 py-1.5 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs text-[var(--fop-text)]"
              />
            </div>
            <div className="flex-1 overflow-y-auto border border-[var(--fop-border)] rounded p-1 max-h-72">
              {filtered.length === 0 ? (
                <div className="text-[11px] text-[var(--fop-text-4)] p-3 text-center">No matches</div>
              ) : (
                <ul className="space-y-0.5">
                  {filtered.map((s) => {
                    const isSel = selected.has(s.id);
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => toggle(s.id)}
                          data-testid={`pkg-sheet-${s.id}`}
                          className={"w-full text-left px-2 py-1.5 rounded text-[11px] font-mono flex items-center gap-2 " +
                            (isSel ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]" : "text-[var(--fop-text-2)] hover:bg-[var(--fop-surface)]")}
                        >
                          {isSel ? <Check size={11} /> : <span className="w-[11px]" />}
                          <span className="font-bold">{s.sheet_number}</span>
                          <span className="truncate text-[var(--fop-text-3)]">{s.sheet_title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[var(--fop-border)] flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={saving} className="btn btn-ghost text-xs">Cancel</button>
          <button onClick={submit} disabled={saving || !editing.name?.trim()} data-testid="pkg-save" className="btn btn-primary text-xs inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save package
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareModal({ packageId, name, canWrite, onClose }) {
  const [link, setLink] = useState(null);
  const [working, setWorking] = useState(false);
  const [ttl, setTtl] = useState(14);

  const create = async () => {
    setWorking(true);
    try {
      const r = await api.post(`/sheet-packages/${packageId}/share`, { expires_in_days: ttl });
      const url = `${window.location.origin}/share/${r.data.token}`;
      setLink({ ...r.data, full_url: url });
      toast.success("Share link created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setWorking(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke all share links for this package?")) return;
    setWorking(true);
    try {
      await api.post(`/sheet-packages/${packageId}/share/revoke`);
      setLink(null);
      toast.success("All share links revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setWorking(false); }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.full_url);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="fop-surface-2 rounded-lg p-5 w-full max-w-lg border border-[var(--fop-border-hi)]" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-lg text-[var(--fop-text)]">Share Package</h3>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={16} /></button>
        </div>
        <p className="text-xs text-[var(--fop-text-3)] mb-4 truncate">{name}</p>

        {!link ? (
          <>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Expires in (days)</label>
            <input
              type="number" min="1" max="90"
              value={ttl}
              onChange={(e) => setTtl(Math.max(1, Math.min(90, Number(e.target.value) || 14)))}
              data-testid="share-ttl"
              className="w-32 px-3 py-2 mb-4 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)]"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={onClose} className="btn btn-ghost text-xs">Cancel</button>
              {canWrite && (
                <button onClick={create} disabled={working} data-testid="create-share-link" className="btn btn-primary text-xs inline-flex items-center gap-1.5">
                  {working ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />} Create link
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Share URL</label>
            <div className="flex items-center gap-2 mb-3">
              <input
                value={link.full_url}
                readOnly
                data-testid="share-url"
                className="flex-1 px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs font-mono text-[var(--fop-text)]"
              />
              <button onClick={copy} className="btn btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="copy-share-link">
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="text-[11px] font-mono text-[var(--fop-text-4)] mb-4">
              Expires {fmtDate(link.expires_at)} · open in new tab <a className="text-[var(--fop-primary)] hover:underline ml-1 inline-flex items-center gap-0.5" href={link.full_url} target="_blank" rel="noreferrer"><ExternalLink size={10} /></a>
            </div>
            <div className="flex items-center justify-between gap-2">
              {canWrite && (
                <button onClick={revoke} disabled={working} className="btn btn-ghost text-xs text-red-400 inline-flex items-center gap-1.5" data-testid="revoke-share-link">
                  <ShieldX size={11} /> Revoke all
                </button>
              )}
              <button onClick={onClose} className="btn btn-primary text-xs ml-auto">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
